/**
 * Timezone Store - Timezone Management
 *
 * Manages application timezone.
 * Provides hooks for accessing and updating the current timezone.
 */

import { DEFAULT_TIMEZONE } from "@/config/timezone-config";
import { createStore, withLogging } from "../../core";
import type { CompleteTimezoneStoreState, TimezoneStoreState } from "../../types/timezone";

// Initial state
const initialState: Omit<TimezoneStoreState, "_initialized" | "_lastUpdated"> = {
  timezone: DEFAULT_TIMEZONE,
};

// Create the timezone store
export const useTimezoneStore = createStore<CompleteTimezoneStoreState>(
  withLogging("TimezoneStore", (set) => ({
    ...initialState,
    _initialized: false,
    _lastUpdated: Date.now(),

    // Set timezone
    setTimezone: (timezone: string) =>
      set((state) => {
        // Only update if timezone actually changed
        if (state.timezone === timezone) {
          return state;
        }
        return {
          ...state,
          timezone,
          _lastUpdated: Date.now(),
        };
      }),
  })),
  {
    name: "timezone-store",
    persist: true,
    version: 2, // Increment version for migration
    partialize: (state) => ({
      timezone: state.timezone,
    }),
  },
);

// ===== SELECTORS =====

/**
 * Get complete timezone store state
 */
export const useTimezoneState = () => useTimezoneStore((state) => state);

/**
 * Get current timezone
 */
export const useCurrentTimezone = () => useTimezoneStore((state) => state.timezone);

// ===== ACTION HOOKS =====

/**
 * Get timezone actions
 */
export const useTimezoneActions = () =>
  useTimezoneStore((state) => ({
    setTimezone: state.setTimezone,
  }));

/**
 * Get timezone value directly (non-hook, for use outside React components)
 */
export const getTimezone = () => useTimezoneStore.getState().timezone;

/**
 * Set timezone directly (non-hook, for use outside React components)
 */
export const setTimezone = (timezone: string) => useTimezoneStore.getState().setTimezone(timezone);
